import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail } from 'lucide-react';
import { useSite } from '../useSite';
import { SERVICE_KEYS } from '../routes';
import { BrandLockup } from './Brand';
import { Container } from './Primitives';

const Col = ({ title, links }) => (
  <div>
    <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-tan">{title}</p>
    <ul className="space-y-2.5">
      {links.map((l) => (
        <li key={l.to}><Link to={l.to} data-testid={l.testId} className="text-sm text-bone/80 hover:text-bone-light">{l.label}</Link></li>
      ))}
    </ul>
  </div>
);

export const Footer = () => {
  const { t, p } = useSite();
  const year = new Date().getFullYear();
  return (
    <footer className="bg-kombu-green text-bone-light" data-testid="site-footer">
      <Container className="py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <BrandLockup light />
            <p className="mt-6 font-serif text-xl italic text-tan">{t.brand.tagline}</p>
            <div className="mt-6 space-y-2 text-sm text-bone/80">
              {t.founders.map((f) => (
                <a key={f.email} href={`mailto:${f.email}`} className="flex items-center gap-2 break-all hover:text-bone-light">
                  <Mail className="h-4 w-4 flex-shrink-0 text-tan" />{f.email}
                </a>
              ))}
              <a href="https://instagram.com/bullish_accounting" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="flex items-center gap-2 hover:text-bone-light">
                <Instagram className="h-4 w-4 text-tan" />@bullish_accounting
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-10 md:col-span-8 md:grid-cols-3">
            <Col title={t.footer.expertise} links={SERVICE_KEYS.map((k) => ({ to: p(k), label: t.services[k].name }))} />
            <Col
              title={t.footer.firm}
              links={[
                { to: p('investors'), label: t.audiences.investors.name },
                { to: p('owners'), label: t.audiences.owners.name },
                { to: p('plans'), label: t.nav.plans },
                { to: p('howWeWork'), label: t.nav.howWeWork },
                { to: p('about'), label: t.nav.about },
                { to: p('book'), label: t.nav.book },
                { to: p('review'), label: t.footer.review, testId: 'footer-review-link' },
              ]}
            />
            <Col
              title={t.footer.legal}
              links={[
                { to: p('privacy'), label: t.legal.privacy.h1 },
                { to: p('terms'), label: t.legal.terms.h1 },
                { to: p('disclaimer'), label: t.legal.disclaimer.h1 },
              ]}
            />
          </div>
        </div>
        <div className="mt-14 flex flex-col gap-6 border-t border-bone-light/15 pt-8 md:flex-row md:items-end md:justify-between">
          <p className="max-w-3xl text-xs leading-relaxed text-bone/60" data-testid="footer-disclaimer">{t.footer.disclaimer}</p>
          <p className="shrink-0 text-xs text-bone/60" data-testid="footer-copyright">&copy; {year} {t.brand.legal}. {t.footer.rights}</p>
        </div>
      </Container>
    </footer>
  );
};
